import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "#1F1F1F",
      color: "#F5F5F5",
      minWidth: "400px",
    },
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: "1px solid #656565",
  },
  formControl: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  radioGroup: {
    "& .MuiFormControlLabel-root": {
      color: "#F5F5F5",
      marginBottom: theme.spacing(1),
    },
    "& .MuiRadio-root": {
      color: "#6F9CEB",
    },
    "& .MuiRadio-root.Mui-checked": {
      color: "#6F9CEB",
    },
  },
  textField: {
    "& .MuiInputBase-root": {
      color: "#F5F5F5",
    },
    "& .MuiInputLabel-root": {
      color: "#F5F5F5",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#656565",
      },
      "&:hover fieldset": {
        borderColor: "#6F9CEB",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6F9CEB",
      },
    },
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  alert: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    fontSize: 14,
  },
  warning: {
    background: "#2d2d2d",
    border: "1px solid #ff9800",
    color: "#ff9800",
  },
  info: {
    background: "#232323",
    border: "1px solid #2196f3",
    color: "#90caf9",
  },
}));

const SKIP_REASONS = [
  { value: "traffic_situation", label: "Traffic Situation", isVisibleInStopOptions: false },
  { value: "order_cancelled", label: "Order Cancelled", isVisibleInStopOptions: false },
  { value: "cant_deliver_on_time", label: "Can't Deliver On Time", isVisibleInStopOptions: false },
  { value: "no_packages", label: "No Packages to Deliver/Return", isVisibleInStopOptions: true },
  { value: "other", label: "Other", isVisibleInStopOptions: true },
  { value: "other", label: "Other", isVisibleInStopOptions: false },
];

const SkipStopModal = ({ open, onClose, onSkipStop, showStopOptions, customerName }) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");

  const handleClose = () => {
    setSelectedReason("");
    setOtherReason("");
    onClose();
  };

  const handleReasonChange = (event) => {
    setSelectedReason(event.target.value);
  };

  const handleSkipStop = () => {
    const reason = selectedReason === "other" ? otherReason : selectedReason;
    onSkipStop(reason);
    handleClose();
  };

  const isSkipDisabled = () => {
    return !selectedReason || (selectedReason === "other" && !otherReason.trim());
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth className={classes.dialog}>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="h6" gutterBottom>
          {t("Stop will be skipped permanently and excluded from the route.")}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {t("You will be routed to the next stop.")}
        </Typography>

        <FormControl component="fieldset" className={classes.formControl}>
          <Typography variant="subtitle1" gutterBottom>
            {t("Please select a reason")}:
          </Typography>
          <RadioGroup value={selectedReason} onChange={handleReasonChange} className={classes.radioGroup}>
            {SKIP_REASONS
              .filter((r) => (showStopOptions ? r.isVisibleInStopOptions : !r.isVisibleInStopOptions))
              .map((reason) => (
                <FormControlLabel
                  key={reason.value}
                  value={reason.value}
                  control={<Radio />}
                  label={t(reason.label)}
                />
              ))}
          </RadioGroup>
        </FormControl>

        {selectedReason === "other" && (
          <TextField
            fullWidth
            label={t("Please specify the reason")}
            value={otherReason}
            onChange={(e) => setOtherReason(e.target.value)}
            multiline
            rows={3}
            className={classes.textField}
            variant="outlined"
          />
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} color="primary">
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleSkipStop}
          color="primary"
          variant="contained"
          disabled={isSkipDisabled()}
          className={classes.button}
        >
          {t("Skip Stop")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkipStopModal;
